import { Link, useParams, Navigate } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, CheckCircle2, ArrowRight, ChevronDown } from "lucide-react";
import { SERVICES, PROJECTS, TESTIMONIALS, FAQS } from "../data/site";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);

  if (!service) return <Navigate to="/services" replace />;

  const relatedProjects = PROJECTS.slice(0, 3);
  const t = TESTIMONIALS[0];

  return (
    <>
      <PageHero
        eyebrow={`Service · ${service.code}`}
        title={service.title}
        subtitle={service.short}
        image={service.image}
      />

      {/* Overview + Capabilities */}
      <section className="py-24 md:py-32 container-wide grid md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-5">
          <div className="eyebrow mb-6">Overview</div>
          <h2 className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tight">A complete engineering capability.</h2>
        </Reveal>
        <Reveal className="md:col-span-6 md:col-start-7" delay={150}>
          <p className="text-base md:text-lg leading-relaxed text-[#333]">UMD's {service.title.toLowerCase()} practice brings together design, manufacturing, deployment and support under one accountable team. We are the partner clients choose when the project cannot fail and the timeline cannot slip.</p>
          <div className="mt-10 grid grid-cols-2 gap-px bg-[var(--border)]">
            {service.capabilities.map((c, i) => (
              <div key={i} className="bg-white p-5 flex items-start gap-3">
                <CheckCircle2 size={18} className="text-[var(--primary)] mt-0.5 shrink-0" />
                <span className="text-sm">{c}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Benefits / Industries */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] text-white relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="container-wide relative grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="eyebrow eyebrow-light mb-6">Outcomes</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] tracking-tight">Engineered for measurable impact.</h2>
          </Reveal>
          <div className="md:col-span-6 md:col-start-7 space-y-6">
            {[
              { n: "↓41%", t: "Unplanned downtime", d: "Average operational uplift across deployed clients." },
              { n: "↑99.9%", t: "Asset visibility", d: "Real-time accuracy across nationwide rollouts." },
              { n: "4.5x", t: "ROI in 18 months", d: "Typical payback window for IoT modernisation programs." },
            ].map((b, i) => (
              <Reveal key={i} delay={i * 100} className="flex items-baseline gap-8 py-6 border-b border-white/10">
                <div className="font-display text-4xl md:text-5xl text-[var(--primary)] tracking-tight w-32 shrink-0">{b.n}</div>
                <div>
                  <div className="font-display text-xl tracking-tight">{b.t}</div>
                  <div className="text-sm text-white/60 mt-1">{b.d}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Related projects */}
      <section className="py-24 md:py-32 container-wide">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="eyebrow mb-6">Related work</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] tracking-tight">Recent deployments.</h2>
          </div>
          <Link to="/projects" className="btn-ghost">All projects <ArrowRight size={14}/></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {relatedProjects.map((p) => (
            <Link key={p.slug} to="/projects" data-testid={`detail-related-${p.slug}`} className="proj-card block aspect-[4/5] relative">
              <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="proj-mask absolute inset-0" />
              <div className="absolute top-4 left-4 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/80">{p.category}</div>
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <h3 className="font-display text-lg md:text-xl leading-tight">{p.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 bg-[#fafafa]">
        <div className="container-wide max-w-5xl">
          <p className="font-display text-2xl md:text-4xl leading-[1.15] tracking-tight">"{t.quote}"</p>
          <div className="mt-8 font-mono-tech text-xs tracking-[0.18em] uppercase text-[var(--muted-fg)]">
            — {t.name}, {t.role}, {t.company}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 md:py-32 container-wide grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="eyebrow mb-6">Frequently asked</div>
          <h2 className="font-display text-4xl md:text-5xl leading-[0.95] tracking-tight">Questions, answered.</h2>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          {FAQS.map((f, i) => (
            <div key={i} className="border-b border-[var(--border)]">
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-6 py-6 text-left"
              >
                <span className="font-semibold text-base md:text-lg">{f.q}</span>
                <ChevronDown size={20} className={`shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-500 ${openFaq === i ? "max-h-40 pb-6" : "max-h-0"}`}>
                <p className="text-[var(--muted-fg)] leading-relaxed">{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Enquiry CTA */}
      <section className="py-24 md:py-32 bg-[#08142b] text-white relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="container-wide relative grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <div className="eyebrow eyebrow-light mb-6">Discuss {service.title}</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">Bring us your<br/><span className="text-[var(--primary)]">hardest problem.</span></h2>
          </div>
          <div className="md:col-span-4">
            <Link to="/contact" data-testid="service-cta-enquire" className="btn-primary justify-between w-full">Start the conversation <ArrowUpRight size={18} className="arrow"/></Link>
          </div>
        </div>
      </section>
    </>
  );
}
